import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe, ArrowLeft, FileText } from 'lucide-react';
import { Button, LanguageModal } from '@/components/ui';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import React from 'react';
import { PageSeo } from '@/components/seo/PageSeo';

function renderInlineBold(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const start = remaining.indexOf('**');
    if (start === -1) {
      parts.push(remaining);
      break;
    }
    const end = remaining.indexOf('**', start + 2);
    if (end === -1) {
      parts.push(remaining);
      break;
    }
    if (start > 0) parts.push(remaining.slice(0, start));
    parts.push(<strong key={key++}>{remaining.slice(start + 2, end)}</strong>);
    remaining = remaining.slice(end + 2);
  }
  return parts;
}

const content = {
  hr: {
    title: 'Uvjeti korištenja',
    lastUpdated: 'Zadnje ažurirano: 6. veljače 2026.',
    intro: 'Dobrodošli u BookiApp. Korištenjem naše usluge pristajete na ove uvjete korištenja.',
    sections: [
      {
        title: '1. Prihvaćanje uvjeta',
        content: `Pristupanjem ili korištenjem BookiApp usluge ("Usluga"), pristajete biti obvezani ovim Uvjetima korištenja. Ako se ne slažete s bilo kojim dijelom uvjeta, nemate pravo pristupiti Usluzi.`,
      },
      {
        title: '2. Opis usluge',
        content: `BookiApp je platforma za upravljanje gostoprimstvom koja omogućuje:

- Upravljanje smještajnim objektima i jedinicama
- Praćenje rezervacija
- Komunikaciju s gostima putem chata s automatskim prijevodom
- Dijeljenje informacija o boravku putem app za goste
- Push obavijesti za domaćine i goste
- Smart Inbox: opcionalna integracija s Gmailom/Outlookom za upravljanje upitima gostiju iz BookiAppa (detalji o korištenju Google podataka u Politici privatnosti)

Usluga je namijenjena iznajmljivačima apartmana, vila, soba i sličnih smještajnih kapaciteta.`,
      },
      {
        title: '3. Registracija računa',
        content: `Za korištenje Usluge morate:

- Imati najmanje 18 godina
- Pružiti točne i potpune informacije
- Održavati sigurnost svoje lozinke
- Odmah nas obavijestiti o neovlaštenom korištenju

Odgovorni ste za sve aktivnosti koje se odvijaju pod vašim računom.`,
      },
      {
        title: '4. Pretplata i plaćanje',
        content: `**Plaćanje:**
- Pretplata se naplaćuje unaprijed (godišnje)
- Cijene su izražene u eurima (EUR)
- Plaćanje se vrši putem Stripe-a

**Pravo na povrat novca:**
- Sukladno Zakonu o zaštiti potrošača, imate pravo na povrat novca unutar 14 dana od kupnje
- Za povrat kontaktirajte nas na admin@bookiapp.com

**Otkazivanje:**
- Možete otkazati pretplatu u bilo kojem trenutku
- Pristup ostaje do kraja plaćenog razdoblja

**Promjene cijena:**
Zadržavamo pravo promjene cijena uz obavijest 30 dana unaprijed. Early Adopter cijena ostaje zauvijek zaključana.`,
      },
      {
        title: '5. Prihvatljivo korištenje',
        content: `Slažete se da nećete:

- Koristiti Uslugu za nezakonite svrhe
- Slati neželjenu poštu ili zlonamjerni sadržaj
- Pokušavati neovlašteno pristupiti sustavima
- Ometati ili narušavati Uslugu
- Koristiti Uslugu za prijevare
- Kršiti prava intelektualnog vlasništva

Zadržavamo pravo suspendirati ili ukinuti račun za kršenje ovih uvjeta.`,
      },
      {
        title: '6. Sadržaj korisnika',
        content: `**Vaš sadržaj:**
Zadržavate vlasništvo nad sadržajem koji učitavate (slike, tekstovi, podaci o objektima).

**Licenca:**
Davanjem sadržaja, dajete nam licencu za korištenje, pohranjivanje i prikazivanje sadržaja u svrhu pružanja Usluge.

**Odgovornost:**
Vi ste odgovorni za zakonitost i točnost sadržaja koji objavljujete.`,
      },
      {
        title: '7. Intelektualno vlasništvo',
        content: `BookiApp, uključujući softver, dizajn, logotipe i sadržaj, zaštićen je autorskim pravima i drugim pravima intelektualnog vlasništva.

Nije dopušteno kopiranje, modificiranje ili distribucija bez našeg pisanog odobrenja.`,
      },
      {
        title: '8. Ograničenje odgovornosti',
        content: `**Usluga "kakva jest":**
Usluga se pruža "kakva jest" i "kako je dostupna", bez jamstava bilo koje vrste.

**Ograničenje:**
U najvećoj mjeri dopuštenoj zakonom, BookiApp neće biti odgovoran za:
- Indirektne, posebne ili posljedične štete
- Gubitak profita, podataka ili poslovne prilike
- Štete nastale korištenjem ili nemogućnošću korištenja Usluge

**Maksimalna odgovornost:**
Naša ukupna odgovornost ograničena je na iznos koji ste platili za Uslugu u posljednjih 12 mjeseci.`,
      },
      {
        title: '9. Privatnost',
        content: `Korištenje vaših osobnih podataka regulirano je našom Politikom privatnosti, koja je sastavni dio ovih Uvjeta.`,
      },
      {
        title: '10. Izmjene uvjeta',
        content: `Možemo izmijeniti ove Uvjete u bilo kojem trenutku. O značajnim promjenama ćemo vas obavijestiti putem emaila ili obavijesti u aplikaciji najmanje 30 dana unaprijed.

Nastavak korištenja Usluge nakon izmjena znači prihvaćanje novih Uvjeta.`,
      },
      {
        title: '11. Raskid',
        content: `**Vaš raskid:**
Možete zatvoriti svoj račun u bilo kojem trenutku putem postavki računa.

**Naš raskid:**
Možemo suspendirati ili ukinuti vaš pristup ako:
- Kršite ove Uvjete
- Ne platite pretplatu
- Usluga se ukida

**Posljedice raskida:**
Po raskidu, vaši podaci će biti obrisani u skladu s našom Politikom privatnosti.`,
      },
      {
        title: '12. Mjerodavno pravo',
        content: `Ovi Uvjeti podliježu zakonima Republike Hrvatske. Svi sporovi rješavat će se pred nadležnim sudovima u Hrvatskoj.`,
      },
      {
        title: '13. Kontakt',
        content: `Za pitanja o ovim Uvjetima:

Email: admin@bookiapp.com
Web: https://bookiapp.com/contact`,
      },
    ],
  },
  en: {
    title: 'Terms of Service',
    lastUpdated: 'Last updated: February 6, 2026',
    intro: 'Welcome to BookiApp. By using our service, you agree to these terms of service.',
    sections: [
      {
        title: '1. Acceptance of Terms',
        content: `By accessing or using the BookiApp service ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of the terms, you may not access the Service.`,
      },
      {
        title: '2. Description of Service',
        content: `BookiApp is a hospitality management platform that enables:

- Management of accommodation properties and units
- Booking tracking
- Guest communication via chat with automatic translation
- Sharing stay information via Guest App
- Push notifications for hosts and guests
- Smart Inbox: optional Gmail/Outlook integration to manage guest inquiries from inside BookiApp (details on use of Google data in the Privacy Policy)

The Service is intended for hosts of apartments, villas, rooms and similar accommodation.`,
      },
      {
        title: '3. Account Registration',
        content: `To use the Service you must:

- Be at least 18 years old
- Provide accurate and complete information
- Maintain the security of your password
- Immediately notify us of unauthorized use

You are responsible for all activities that occur under your account.`,
      },
      {
        title: '4. Subscription and Payment',
        content: `**Payment:**
- Subscription is charged in advance (yearly)
- Prices are in Euros (EUR)
- Payment is processed through Stripe

**Right of Withdrawal:**
- In accordance with consumer protection laws, you have the right to a refund within 14 days of purchase
- For refunds, contact us at admin@bookiapp.com

**Cancellation:**
- You can cancel your subscription at any time
- Access remains until the end of the paid period

**Price Changes:**
We reserve the right to change prices with 30 days notice. Early Adopter pricing is locked forever.`,
      },
      {
        title: '5. Acceptable Use',
        content: `You agree not to:

- Use the Service for illegal purposes
- Send spam or malicious content
- Attempt unauthorized access to systems
- Disrupt or impair the Service
- Use the Service for fraud
- Violate intellectual property rights

We reserve the right to suspend or terminate accounts for violating these terms.`,
      },
      {
        title: '6. User Content',
        content: `**Your Content:**
You retain ownership of content you upload (images, text, property data).

**License:**
By providing content, you grant us a license to use, store and display the content for providing the Service.

**Responsibility:**
You are responsible for the legality and accuracy of content you post.`,
      },
      {
        title: '7. Intellectual Property',
        content: `BookiApp, including software, design, logos and content, is protected by copyright and other intellectual property rights.

Copying, modifying or distributing without our written permission is not permitted.`,
      },
      {
        title: '8. Limitation of Liability',
        content: `**Service "As Is":**
The Service is provided "as is" and "as available", without warranties of any kind.

**Limitation:**
To the maximum extent permitted by law, BookiApp will not be liable for:
- Indirect, special or consequential damages
- Loss of profit, data or business opportunity
- Damages from use or inability to use the Service

**Maximum Liability:**
Our total liability is limited to the amount you paid for the Service in the last 12 months.`,
      },
      {
        title: '9. Privacy',
        content: `Use of your personal data is governed by our Privacy Policy, which is an integral part of these Terms.`,
      },
      {
        title: '10. Changes to Terms',
        content: `We may modify these Terms at any time. We will notify you of significant changes via email or in-app notification at least 30 days in advance.

Continued use of the Service after changes means acceptance of the new Terms.`,
      },
      {
        title: '11. Termination',
        content: `**Your Termination:**
You can close your account at any time through account settings.

**Our Termination:**
We may suspend or terminate your access if:
- You violate these Terms
- You don't pay subscription
- The Service is discontinued

**Consequences of Termination:**
Upon termination, your data will be deleted in accordance with our Privacy Policy.`,
      },
      {
        title: '12. Governing Law',
        content: `These Terms are governed by the laws of the Republic of Croatia. All disputes will be resolved before competent courts in Croatia.`,
      },
      {
        title: '13. Contact',
        content: `For questions about these Terms:

Email: admin@bookiapp.com
Web: https://bookiapp.com/contact`,
      },
    ],
  },
};

export function TermsPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  const [langOpen, setLangOpen] = useState(false);
  const { isSignedIn } = useAuth();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <PageSeo path="/terms" title={`${t.title} | BookiApp`} description={t.intro} />
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} aria-haspopup="dialog" aria-expanded={langOpen}>
              <Globe className="h-4 w-4 mr-2" />
              {i18n.language.toUpperCase()}
            </Button>
            {isSignedIn ? (
              <>
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0">
                    Dashboard
                  </Button>
                </Link>
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
                />
              </>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                  {lang === 'hr' ? 'Prijava' : 'Sign In'}
                </Button>
              </SignInButton>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {lang === 'hr' ? 'Natrag' : 'Back'}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center">
            <FileText className="h-6 w-6 text-cyan-500" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{t.title}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.lastUpdated}</p>
          </div>
        </div>

        <p className="text-[hsl(var(--muted-foreground))] mb-8">{t.intro}</p>

        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {section.content.split('\n').map((line, j) => {
                  if (!line.trim()) return null;
                  if (line.startsWith('- ')) {
                    return <p key={j} className="ml-4 text-[hsl(var(--muted-foreground))]">- {renderInlineBold(line.slice(2))}</p>;
                  }
                  if (line.includes('**')) {
                    return <p key={j} className="text-[hsl(var(--muted-foreground))] mt-2">{renderInlineBold(line)}</p>;
                  }
                  return <p key={j} className="text-[hsl(var(--muted-foreground))]">{line}</p>;
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {lang === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default TermsPage;
